/**
 * Сортируемая таблица общей статистики Instagram аккаунтов
 */

import { Avatar, AvatarFallback, AvatarImage } from '../../../components/ui/avatar';
import { Badge } from '../../../components/ui/badge';
import { Skeleton } from '../../../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { cn } from '../../../components/ui/utils';

import { Search } from 'lucide-react';

import { getCountryName } from '../lib/country-names';
import { getCityDisplayName } from '../lib/city-names';
import { SortableTableHeader } from './SortableTableHeader';
import { PlatformBadge } from '../ui/PlatformBadge';


import type { AccountStatsRow, SortDirection, SortKey } from '../types/account-stats';
import type { DemographicsFilter } from '../types/demographics-filter';
import type { JSX, ReactNode } from 'react';

type AccountsStatsTableProps = {
  rows: AccountStatsRow[];
  sortKey: SortKey;
  sortDirection: SortDirection;
  onSort: (key: SortKey) => void;
  isLoadingAccounts: boolean;
  showDeltas: boolean;
  onRowClick?: (accountId: string) => void;
  toolbar?: ReactNode;
  activeFilter?: DemographicsFilter | null;
  onFilterReset?: () => void;
  unavailableDemographicsIds?: Set<string>;
};

type DeltaBadgeProps = {
  delta: number | null;
  format?: 'number' | 'percent';
};

function FilterIndicatorStrip({
  filter,
  onReset,
}: {
  filter: DemographicsFilter;
  onReset: () => void;
}): JSX.Element {
  const parts: string[] = [];

  if (filter.countries.length > 0) {
    const names = filter.countries.map(getCountryName);
    const label =
      names.slice(0, 2).join(', ') + (names.length > 2 ? ` +${names.length - 2}` : '');
    parts.push(label);
  }
  if (filter.cities.length > 0) {
    const names = filter.cities.map(getCityDisplayName);
    const label =
      names.slice(0, 2).join(', ') + (names.length > 2 ? ` +${names.length - 2}` : '');
    parts.push(label);
  }
  if (filter.gender) parts.push(filter.gender === 'F' ? 'Женщины' : 'Мужчины');
  if (filter.ageRanges.length > 0) parts.push(filter.ageRanges.join(', '));

  return (
    <div className="flex items-center gap-2 px-4 py-2 bg-primary/5 border-b text-sm text-primary">
      <Search className="size-4 text-primary shrink-0" />
      <span className="flex-1">Демография: {parts.join(' · ')}</span>
      <button
        onClick={onReset}
        className="text-primary hover:text-primary/80 font-semibold text-base leading-none"
        aria-label="Сбросить фильтр"
      >
        ×
      </button>
    </div>
  );
}

function DeltaBadge({ delta, format = 'number' }: DeltaBadgeProps): JSX.Element | null {
  if (delta === null) return null;

  const threshold = format === 'percent' ? 0.001 : 0.5;
  if (Math.abs(delta) < threshold) return null;

  const isPositive = delta > 0;
  const abs = Math.abs(delta);
  const formatted = format === 'percent' ? `${abs.toFixed(2)}%` : abs.toLocaleString('ru-RU');

  return (
    <span
      className={`text-xs font-medium tabular-nums ml-1 ${
        isPositive ? 'text-emerald-600' : 'text-red-500'
      }`}
    >
      {isPositive ? '+' : '−'}
      {formatted}
    </span>
  );
}

function MetricCell({
  value,
  delta,
  isLoading,
}: {
  value: number | null;
  delta: number | null;
  isLoading: boolean;
}): JSX.Element {
  if (isLoading) {
    return (
      <div className="flex flex-col items-end gap-0.5">
        <Skeleton className="h-4 w-16" />
      </div>
    );
  }

  if (value === null) {
    return <span className="text-muted-foreground">—</span>;
  }

  return (
    <div className="flex flex-col items-end">
      <span className="tabular-nums font-medium">{value.toLocaleString('ru-RU')}</span>
      <DeltaBadge delta={delta} />
    </div>
  );
}

function TotalCell({ value, delta }: { value: number | null; delta: number | null }): JSX.Element {
  if (value === null) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="flex flex-col items-end">
      <span className="tabular-nums">{value.toLocaleString('ru-RU')}</span>
      <DeltaBadge delta={delta} />
    </div>
  );
}

function ErCell({
  er,
  erDelta,
  isLoading,
}: {
  er: number | null;
  erDelta: number | null;
  isLoading: boolean;
}): JSX.Element {
  if (isLoading) {
    return <Skeleton className="h-4 w-12 ml-auto" />;
  }

  if (er === null) {
    return <span className="text-muted-foreground">—</span>;
  }

  return (
    <div className="flex flex-col items-end">
      <span className="tabular-nums font-medium">{er.toFixed(2)}%</span>
      <DeltaBadge delta={erDelta} format="percent" />
    </div>
  );
}

function AccountCell({
  row,
  demographicsUnavailable,
}: {
  row: AccountStatsRow;
  demographicsUnavailable?: boolean;
}): JSX.Element {
  const initials = row.username.slice(0, 2).toUpperCase();

  return (
    <div className="flex items-center gap-3 min-w-0">
      <Avatar className="size-9 shrink-0">
        <AvatarImage src={row.avatarUrl ?? undefined} alt={row.username} />
        <AvatarFallback className="text-xs font-semibold">{initials}</AvatarFallback>
      </Avatar>
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="font-medium text-sm truncate">{row.username}</span>
          <PlatformBadge platform={row.platform === 'tiktok' ? 'tiktok' : 'instagram'} />
          {demographicsUnavailable && (
            <span
              title="Демографические данные недоступны"
              className="text-amber-500 text-xs cursor-help shrink-0"
            >
              ⚠️
            </span>
          )}
        </div>
        {row.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-0.5">
            {row.tags.map(tag => (
              <Badge key={tag} variant="secondary" className="text-[10px] px-1.5 py-0 h-4">
                {tag}
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SkeletonRow(): JSX.Element {
  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-3">
          <Skeleton className="size-9 rounded-full shrink-0" />
          <div className="flex flex-col gap-1">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-3 w-20" />
          </div>
        </div>
      </TableCell>
      {Array.from({ length: 6 }).map((_, i) => (
        <TableCell key={i} className="text-right">
          <Skeleton className="h-4 w-16 ml-auto" />
        </TableCell>
      ))}
    </TableRow>
  );
}

function sumOrNull(values: (number | null)[]): number | null {
  const loaded = values.filter((v): v is number => v !== null);
  return loaded.length > 0 ? loaded.reduce((a, b) => a + b, 0) : null;
}

function computeTotals(rows: AccountStatsRow[]) {
  const ready = rows.filter(r => !r.isLoading && !r.error);
  const totalImpressions = sumOrNull(ready.map(r => r.impressions));
  const totalInteractions = sumOrNull(ready.map(r => r.interactions));
  return {
    followers: sumOrNull(ready.map(r => r.followers)),
    impressions: totalImpressions,
    reach: sumOrNull(ready.map(r => r.reach)),
    interactions: totalInteractions,
    // Сумма приростов по строкам: у подписчиков — новые за период,
    // у остальных — разница с прошлым периодом.
    followersDelta: sumOrNull(ready.map(r => r.followersDelta)),
    impressionsDelta: sumOrNull(ready.map(r => r.impressionsDelta)),
    reachDelta: sumOrNull(ready.map(r => r.reachDelta)),
    interactionsDelta: sumOrNull(ready.map(r => r.interactionsDelta)),
  };
}

export function AccountsStatsTable({
  rows,
  sortKey,
  sortDirection,
  onSort,
  isLoadingAccounts,
  showDeltas,
  onRowClick,
  toolbar,
  activeFilter,
  onFilterReset,
  unavailableDemographicsIds,
}: AccountsStatsTableProps): JSX.Element {
  if (isLoadingAccounts) {
    return (
      <div>
        {/* overflow-x-visible removes the BFC created by overflow-x-auto so sticky thead
            defers to AnalyticsPage's scroll container. Trade-off: no per-table h-scroll bar. */}
        <Table containerClassName="overflow-x-visible">
          <TableHeader className="sticky top-14 z-10">
            {toolbar && (
              <tr style={{ border: 'none' }}>
                <th colSpan={7} className="p-0 font-normal text-left bg-background">{toolbar}</th>
              </tr>
            )}
            <TableRow className="bg-background hover:bg-background">
              <TableHead className="w-[260px] bg-background">Аккаунт</TableHead>
              <TableHead className="text-right bg-background">Подписчики</TableHead>
              <TableHead className="text-right bg-background">Показы</TableHead>
              <TableHead className="text-right bg-background">Охваты</TableHead>
              <TableHead className="text-right bg-background">Взаимодействия</TableHead>
              <TableHead className="text-right bg-background">ER показы</TableHead>
              <TableHead className="text-right bg-background">ER охват</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 5 }).map((_, i) => (
              <SkeletonRow key={i} />
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div>
        {toolbar && <div className="bg-background">{toolbar}</div>}
        <div className="flex items-center justify-center py-16">
          <p className="text-muted-foreground text-sm">Нет подключённых аккаунтов</p>
        </div>
      </div>
    );
  }

  const totals = computeTotals(rows);

  return (
    <div>
      {/* overflow-x-visible removes the BFC created by overflow-x-auto so sticky thead
          defers to AnalyticsPage's scroll container. Trade-off: no per-table h-scroll bar. */}
      <Table containerClassName="overflow-x-visible">
        <TableHeader className="sticky top-14 z-10 bg-background shadow-[0_2px_4px_-2px_rgba(0,0,0,0.1)]">
          {toolbar && (
            <tr style={{ border: 'none' }}>
              <th colSpan={7} className="p-0 font-normal text-left bg-background">{toolbar}</th>
            </tr>
          )}
          {activeFilter && onFilterReset && (
            <tr style={{ border: 'none' }}>
              <th colSpan={7} className="p-0 font-normal">
                <FilterIndicatorStrip filter={activeFilter} onReset={onFilterReset} />
              </th>
            </tr>
          )}
          <TableRow className="bg-background hover:bg-background">
            <SortableTableHeader
              label="Аккаунт"
              sortKey="username"
              currentKey={sortKey}
              direction={sortDirection}
              onSort={onSort}
              align="start"
              className="w-[260px]"
            />
            <SortableTableHeader
              label="Подписчики"
              sortKey="followers"
              currentKey={sortKey}
              direction={sortDirection}
              onSort={onSort}
            />
            <SortableTableHeader
              label="Показы"
              sortKey="impressions"
              currentKey={sortKey}
              direction={sortDirection}
              onSort={onSort}
            />
            <SortableTableHeader
              label="Охваты"
              sortKey="reach"
              currentKey={sortKey}
              direction={sortDirection}
              onSort={onSort}
            />
            <SortableTableHeader
              label="Взаимодействия"
              sortKey="interactions"
              currentKey={sortKey}
              direction={sortDirection}
              onSort={onSort}
            />
            <SortableTableHeader
              label="ER показы"
              sortKey="er"
              currentKey={sortKey}
              direction={sortDirection}
              onSort={onSort}
            />
            <SortableTableHeader
              label="ER охват"
              sortKey="erByReach"
              currentKey={sortKey}
              direction={sortDirection}
              onSort={onSort}
            />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(row => (
            <TableRow
              key={row.accountId}
              className={cn(
                row.error ? 'opacity-60' : '',
                !row.error && !row.isLoading && onRowClick ? 'cursor-pointer hover:bg-muted/50' : ''
              )}
              onClick={
                !row.error && !row.isLoading && onRowClick
                  ? () => {
                      if (window.getSelection()?.toString()) return;
                      onRowClick(row.accountId);
                    }
                  : undefined
              }
            >
              <TableCell>
                <AccountCell
                  row={row}
                  demographicsUnavailable={
                    !!activeFilter && unavailableDemographicsIds?.has(row.accountId)
                  }
                />
                {row.error && (
                  <p className="text-xs text-destructive mt-1 truncate max-w-[240px]">
                    {row.error}
                  </p>
                )}
              </TableCell>
              <TableCell className="text-right">
                <MetricCell
                  value={row.followers}
                  delta={showDeltas ? row.followersDelta : null}
                  isLoading={row.isLoading}
                />
              </TableCell>
              <TableCell className="text-right">
                <MetricCell
                  value={row.impressions}
                  delta={showDeltas ? row.impressionsDelta : null}
                  isLoading={row.isLoading}
                />
              </TableCell>
              <TableCell className="text-right">
                <MetricCell
                  value={row.reach}
                  delta={showDeltas ? row.reachDelta : null}
                  isLoading={row.isLoading}
                />
              </TableCell>
              <TableCell className="text-right">
                <MetricCell
                  value={row.interactions}
                  delta={showDeltas ? row.interactionsDelta : null}
                  isLoading={row.isLoading}
                />
              </TableCell>
              <TableCell className="text-right">
                <ErCell
                  er={row.er}
                  erDelta={showDeltas ? row.erDelta : null}
                  isLoading={row.isLoading}
                />
              </TableCell>
              <TableCell className="text-right">
                <ErCell
                  er={row.erByReach}
                  erDelta={showDeltas ? row.erByReachDelta : null}
                  isLoading={row.isLoading}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow className="font-semibold bg-muted/40 hover:bg-muted/40">
            <TableCell className="text-sm">Итого</TableCell>
            <TableCell className="text-right text-sm">
              <TotalCell value={totals.followers} delta={showDeltas ? totals.followersDelta : null} />
            </TableCell>
            <TableCell className="text-right text-sm">
              <TotalCell value={totals.impressions} delta={showDeltas ? totals.impressionsDelta : null} />
            </TableCell>
            <TableCell className="text-right text-sm">
              <TotalCell value={totals.reach} delta={showDeltas ? totals.reachDelta : null} />
            </TableCell>
            <TableCell className="text-right text-sm">
              <TotalCell value={totals.interactions} delta={showDeltas ? totals.interactionsDelta : null} />
            </TableCell>
            <TableCell />
            <TableCell />
          </TableRow>
        </TableFooter>
      </Table>
    </div>
  );
}
